<!-- WebsiteContainer.vue -->
<script setup>
import { ref, onMounted, computed } from 'vue'
import { useAuthStore } from '../stores/auth'
import { usePlayerStore } from '../stores/player/playerStore'
import { useWebsiteConfigStore } from '../stores/website/configStore'
import WebsiteViewTerminal from './WebsiteViewTerminal.vue'
import WebsiteViewOnline from './WebsiteViewOnline.vue'
import LoadingView from '../components/LoadingView.vue'
import LoginModal from './LoginModal.vue'
import RegisterModal from './RegisterModal.vue' // Importamos el componente de registro

const authStore = useAuthStore()
const playerStore = usePlayerStore()
const websiteStore = useWebsiteConfigStore()

const isLoading = ref(false)
const showLogin = ref(false)
const showRegister = ref(false) // Nuevo estado para controlar el modal de registro
const registrationSuccess = ref(false) // Estado para indicar registro exitoso
const error = ref('')

const websiteType = computed(() => playerStore.playerConfig?.website_type)
const publicConfig = computed(() => websiteStore.config)

const initializeContainer = async () => {
  try {
    isLoading.value = true
    
    // Primero cargamos la configuración pública
    await websiteStore.loadConfig()
    
    if (authStore.token) {
      await authStore.initializeAuth()
      if (authStore.isAuthenticated) {
        await playerStore.getPlayerConfig()
      } else {
        showLogin.value = true
      }
    } else {
      showLogin.value = true
    }
  } catch (err) {
    console.error('Error in container initialization:', err)
    error.value = err.message
    showLogin.value = true
  } finally {
    isLoading.value = false
  }
}

// Manejo del login exitoso
const handleLoginSuccess = async () => {
  try {
    isLoading.value = true
    showLogin.value = false
    await playerStore.getPlayerConfig()
  } catch (err) {
    console.error('Error after login:', err)
    error.value = err.message
  } finally {
    isLoading.value = false
  }
}

// Manejo del registro exitoso
const handleRegisterSuccess = () => {
  showRegister.value = false
  showLogin.value = true // Mostrar el login después del registro exitoso
  registrationSuccess.value = true // Indicar que viene de un registro exitoso
}

// Función para cambiar al modal de registro
const switchToRegister = () => {
  showLogin.value = false
  showRegister.value = true
  registrationSuccess.value = false // Resetear el estado al cambiar de modal
}

// Función para cambiar al modal de login
const switchToLogin = () => {
  showRegister.value = false
  showLogin.value = true
  // No resetear registrationSuccess aquí para preservar el mensaje si viene de un registro exitoso
}

onMounted(() => {
  initializeContainer()
});
</script>

<template>
  <LoadingView 
    v-if="isLoading"
    message="Loading resources..."
  />
  
  <LoginModal 
    v-else-if="showLogin" 
    @login-success="handleLoginSuccess"
    @switch-to-register="switchToRegister"
    :publicConfig="publicConfig"
    :showRegistrationSuccess="registrationSuccess"
  />
  
  <RegisterModal
    v-else-if="showRegister"
    @register-success="handleRegisterSuccess"
    @switch-to-login="switchToLogin"
  />
  
  <div v-else-if="error" class="error-container">
    {{ error }}
  </div>
  
  <template v-else>
    <WebsiteViewTerminal v-if="websiteType === 'terminal'" />
    <WebsiteViewOnline v-else-if="websiteType === 'online'" />
    <LoadingView v-else message="Determining website type..." />
  </template>
</template>

<style scoped>
@import '../assets/styles/main.css';
@import '../assets/styles/online/OnlineMain.css';

.error-container {
  background-color: rgba(239, 68, 68, 0.2);
  color: rgb(239, 68, 68);
  border: 1px solid rgb(239, 68, 68);
  border-radius: 8px;
  padding: 15px;
  margin: 20px auto;
  max-width: 600px;
  text-align: center;
}
</style>